import React, { FC, useMemo, useState } from 'react'
import { TokenList } from '@components/TokenList/TokenList'
import { Box, Stack, Typography } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { ROUTES } from '@utils/routes'
import useBalances from '@plug-core/hooks/useBalances'
import {
  HiChevronDown,
  HiCog,
  HiUserGroup,
  HiLockClosed,
  HiBookOpen,
  HiSupport,
  HiOutlineDotsHorizontal,
} from 'react-icons/hi'
import { TbLayoutSidebarRight } from 'react-icons/tb'
import { useSelectedChain, useUserTokens } from '@plug-core/state'
import Touchable from '@components/Touchable'
import DropdownMenu, { DropdownSection } from '@components/DropdownMenu'
import { lock } from '@plug-core/handlers/walletActions/wallet'
import { useTranslation } from 'react-i18next'
import { logger } from '@plug-core/logger'
import { PLUG_DOCS_URL, PLUG_SUPPORT_URL } from '@plug-core/constants/urls'
import useWallet from '@plug-core/hooks/useWallet'
import BalanceChange from '@components/BalanceChange/BalanceChange'
import { ActionButtons } from '@components/ActionButtons/ActionButtons'
import { TokenListButtons } from './TokenListButtons'
import { formatMoney } from '@plug-core/utils/formatNumber'
import { LLMChatButton } from '@modules/LLMChat/LLMChatButton'
import ChainPicker, { SUPPORTED_CHAINS } from '@components/ChainPicker'
import { RainbowAvatar } from '@components/RainbowAvatar/RainbowAvatar'
import Promo from '@components/Promo'

export const HomeOverview: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const { groupedBalances } = useBalances()
  const { selectedChain, setSelectedChain } = useSelectedChain()
  const { t } = useTranslation()
  const { wallet } = useWallet()

  const [chainSelectVisible, setChainSelectVisible] = useState(false)

  const sections: DropdownSection[] = useMemo(() => {
    return [
      {
        items: [
          {
            name: t('topbar.settings'),
            icon: HiCog,
            route: ROUTES.SETTINGS,
          },
          {
            name: t('topbar.contacts'),
            icon: HiUserGroup,
            route: ROUTES.CONTACTS,
          },
          { name: t('topbar.lock'), icon: HiLockClosed, action: lock },
        ],
      },
      {
        items: [
          {
            name: t('topbar.documentation'),
            icon: HiBookOpen,
            action: () => window.open(PLUG_DOCS_URL, '_blank'),
          },
          {
            name: t('topbar.support'),
            icon: HiSupport,
            action: () => window.open(PLUG_SUPPORT_URL, '_blank'),
          },
        ],
      },
    ]
  }, [])

  const totalUsdBalance = groupedBalances
    .map((balance) => balance.usdBalance || 0)
    .reduce((acc, balance) => acc + balance, 0)

  const openSidePanel = async () => {
    // Only available in extension environment
    if (!chrome?.windows || !chrome?.tabs || !chrome?.sidePanel) {
      return
    }

    try {
      const currentWindow = await chrome.windows.getCurrent()

      if (currentWindow?.id) {
        const tabs = await chrome.tabs.query({ active: true })
        const currentTab = tabs.find((tab) => tab.windowId === currentWindow.id)

        chrome.sidePanel
          .open({ windowId: currentWindow.id, tabId: currentTab?.id })
          .catch(logger.debug)
      }

      window.close()
    } catch (error) {
      logger.debug('Failed to open side panel:', error)
    }
  }

  return (
    <Stack>
      <LLMChatButton />
      <Stack
        bgcolor="background.default"
        zIndex={location.pathname === ROUTES.HOME ? 1 : 0}
        position="sticky"
        top={0}
        py={1.25}
        px={1.5}
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Touchable onClick={() => navigate(ROUTES.ACCOUNTS)}>
          <Stack flexDirection="row" bgcolor="background.mid" p={0.5} borderRadius={3}>
            <RainbowAvatar size={24} avatar={wallet.emoji || '🚀'} />

            <Stack flexDirection="row" alignItems="center" ml={1} mr={0.5}>
              <Typography fontSize={15} mr={0.5}>
                {wallet.name || 'account1'}
              </Typography>
              <HiChevronDown size={16} />
            </Stack>
          </Stack>
        </Touchable>

        <Stack flexDirection="row">
          <DropdownMenu
            trigger={
              <Box>
                <Touchable
                  scaleFactor={0.15}
                  style={{
                    marginRight: chrome?.sidePanel ? 8 : 0,
                  }}
                >
                  <Stack bgcolor="background.mid" p={1} borderRadius={3}>
                    <HiOutlineDotsHorizontal color="white" size={20} />
                  </Stack>
                </Touchable>
              </Box>
            }
            sections={sections}
          />

          {chrome?.sidePanel && (
            <Touchable scaleFactor={0.15} onClick={openSidePanel}>
              <Stack bgcolor="background.mid" p={1} borderRadius={3}>
                <TbLayoutSidebarRight color="white" size={20} />
              </Stack>
            </Touchable>
          )}
        </Stack>
      </Stack>

      <Stack flex={1}>
        <Stack alignItems="center">
          <Typography variant="h2" mt={2} fontSize={38}>
            {formatMoney(totalUsdBalance)}
          </Typography>
          {wallet && !!totalUsdBalance && <BalanceChange totalUsdBalance={totalUsdBalance} />}
          <Box height={16} />
          <ActionButtons />
        </Stack>

        <Promo />

        <Stack
          mt={2}
          mb={0.25}
          mx={1.5}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography>Tokens</Typography>
          <Touchable onClick={() => setChainSelectVisible(true)}>
            <Stack justifyContent="center" alignItems="center" direction="row">
              <Typography color="text.secondary">{t(`chains.long.${selectedChain}`)}</Typography>
              <HiChevronDown color="text.secondary" size={25} />
            </Stack>
          </Touchable>
          <ChainPicker
            chains={['all', ...SUPPORTED_CHAINS]}
            initialChain={selectedChain}
            chainSelectVisible={chainSelectVisible}
            setChainSelectVisible={setChainSelectVisible}
            onChainChange={setSelectedChain}
          />
        </Stack>
        <Stack>
          <TokenList
            balances={groupedBalances}
            onTokenClick={(token) => navigate(ROUTES.TOKEN_DETAILS(token.token.id))}
            showPriceChange
          />
          <TokenListButtons />
        </Stack>
      </Stack>
    </Stack>
  )
}
